import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React, { Suspense } from 'react';
import { ApolloProvider } from '@common/hooks';
import apollo from 'works/legacy/lib/graph-ql';
import { ErrorBoundary } from './legacy/lib/sentry';
import { loadIntercom } from 'works/legacy/lib/intercom';
import Auth from 'works/legacy/lib/auth';
import {
  AppLayout,
  AppSidebar,
  BrowserRouter,
  ContentSkeleton,
} from '@common/components';
import { AuthGuard } from 'works/legacy/components/auth-guard';
import { OrganizationOnly } from 'works/legacy/components/auth-guard/organization-only';
import { SupplierOnly } from 'works/legacy/components/auth-guard/supplier-only';
import { PublicRoutes } from '@routes/public/public-routes';
import { InactivityModal } from '@identity/components/inactivity-modal';
import { ChunkVersionController } from '@routes/chunk-version-controller';
import { DialogsController } from '@routes/dialogs';
import { SentryMetadataInitializer } from '@identity/components/sentry-metadata-initializer';
import { lazyChunk } from '@routes/services/lazy-chunk-import/lazy-chunk-import';

// Each signed-in user is either an organization or a supplier member (the
// *Only gates render null for the other type), so the whole non-matching route
// tree is dead weight in their bundle. Lazily load each tree so a user only
// fetches their own. The trees now split each page into its own prefetched
// chunk, so the tree chunk itself is small and the landing page is a separate
// fetch — warmed below.
const importOrganizationRoutes = () =>
  import('@routes/organization/organization-routes');
const importSupplierRoutes = () => import('@routes/supplier/supplier-routes');

// Warm the landing-page chunk each membership type renders first, in parallel
// with warming the route tree, so the page split adds no perceived navigation
// latency. The import specifiers (and Mission Control chunk name) match the
// lazy imports in the route trees, so these resolve to the same chunks. Mission
// Control status lives only in the membership query, not localStorage, so an
// org member warms both candidate landings; the unused one is a small chunk
// that would have been prefetched during idle anyway.
const warmOrganizationLanding = () => {
  void import('@institutions/pages/home-page/home-page').catch(() => {});
  void import(
    /* webpackChunkName: "org-mission-control" */ '@institutions/pages/mission-control-staffing-page/mission-control-staffing-page'
  ).catch(() => {});
};
const warmSupplierLanding = () => {
  void import('@institutions/pages/home-page/home-page').catch(() => {});
};

const OrganizationRoutes = lazyChunk(
  importOrganizationRoutes,
  'OrganizationRoutes'
);
const SupplierRoutes = lazyChunk(importSupplierRoutes, 'SupplierRoutes');

// Warm the current user's route-tree and landing-page chunks in parallel with
// the auth/flags queries instead of waiting for the AuthGuard +
// FeatureFlagsProvider waterfall to render the lazy element. membershipType()
// is read synchronously from localStorage; a first-ever load with no stored
// membership simply falls back to the lazy fetch at render time.
if (Auth.organizationMember()) {
  void importOrganizationRoutes().catch(() => {});
  warmOrganizationLanding();
} else if (Auth.supplierMember()) {
  void importSupplierRoutes().catch(() => {});
  warmSupplierLanding();
}

export function App() {
  loadIntercom();

  return (
    <React.StrictMode>
      <ErrorBoundary>
        <ApolloProvider client={apollo}>
          <BrowserRouter>
            <ChunkVersionController />
            <PublicRoutes />
            <AuthGuard>
              <SentryMetadataInitializer />
              <DialogsController />
              <AppLayout sidebar={<AppSidebar />}>
                <Suspense fallback={<ContentSkeleton />}>
                  <OrganizationOnly>
                    <OrganizationRoutes />
                  </OrganizationOnly>
                  <SupplierOnly>
                    <SupplierRoutes />
                  </SupplierOnly>
                </Suspense>
              </AppLayout>
              <InactivityModal />
            </AuthGuard>
          </BrowserRouter>
        </ApolloProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}
